import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CustomizeColumnsModalComponent } from 'app/shared/table/customize-columns-modal.component';
import { ModalsService } from 'app/shared/ui/modals/modals.service';
import { ComparablesControlsComponent } from './comparables-controls.component';
import { Averages, Column, ComparablesTableComponent, OrderChange, RemoveListing } from './comparables-table.component';


@Component({
    selector: 'comparables-container',
    imports: [
        NgTemplateOutlet,
        ComparablesControlsComponent,
        ComparablesTableComponent,
    ],
    template: `
        <ng-template #controls>
            <comparables-controls
                [showDownload]="showDownload"
                [loading]="loading"
                [basemapKey]="basemapKey"
                [(fontSize)]="fontSize"
                (customizeColumns)="onCustomizeColumns()"
                (basemapKeyChange)="onChangeBasemapKey($event)"
                (download)="onDownload()"
                (export)="onExport()">
            </comparables-controls>
        </ng-template>
        <ng-container *ngTemplateOutlet="controls"></ng-container>
        <comparables-table
            [fontSize]="fontSize"
            [columns]="columns"
            [averages]="averages"
            [listings]="listings"
            [orderBy]="orderBy"
            [orderReverse]="orderReverse"
            [selectedListings]="selectedListings"
            (orderChange)="onOrderChange($event)"
            (removeListing)="onRemoveListing($event)"
            (selectListings)="onSelectListings($event)">
        </comparables-table>
        <ng-container *ngTemplateOutlet="controls"></ng-container>
    `,
})
export class ComparablesContainerComponent<T extends { id: string }> implements OnInit {

    @Input() showDownload = false;

    @Input() columns: Column[];

    @Input() defaultOrderedColumns: Column[];

    @Output() customizeColumns = new EventEmitter<{ saveAsPreset: boolean; columns: Column[] }>();

    @Input() averages: Averages;

    @Input() listings: T[];

    orderBy: string;

    orderReverse = false;

    selectedListings: { [id: string]: boolean } = {};

    @Input() basemapKey = 'osm-road';

    @Output() basemapKeyChange = new EventEmitter<string>();

    @Input() loading = false;

    @Output() listingsChange = new EventEmitter<T[]>();

    @Output() downloadListings = new EventEmitter<T[]>();

    @Output() exportListings = new EventEmitter<T[]>();

    @Input() fontSize = 8;

    constructor(private modalsService: ModalsService) {}

    ngOnInit() {
        this.selectedListings = Object.fromEntries(this.listings.map(x => [x.id, true]));
    }

    onChangeBasemapKey(basemapKey: string) {
        this.basemapKeyChange.emit(basemapKey);
    }

    onOrderChange(change: OrderChange<T>) {
        this.orderBy = change.orderBy;
        this.orderReverse = change.orderReverse;
        this.listingsChange.emit(change.listings);
    }

    onRemoveListing(remove: RemoveListing<T>) {
        this.listingsChange.emit(remove.listings);
    }

    onSelectListings(selection: { [idKey: string]: boolean }) {
        this.selectedListings = selection;
    }

    onCustomizeColumns() {
        const { result } = this.modalsService.openModal(
            CustomizeColumnsModalComponent,
            {
                columns: this.columns,
                defaultOrderedColumns: this.defaultOrderedColumns,
            }
        );
        result.subscribe(r => this.customizeColumns.emit(r));
    }

    onDownload() {
        const listings = this.listings.filter(l => this.selectedListings[l.id]);
        this.downloadListings.emit(listings);
    }

    onExport() {
        const listings = this.listings.filter(l => this.selectedListings[l.id]);
        if (listings.length !== 0) {
            this.exportListings.emit(listings);
            return;
        }
        const { result } = this.modalsService.confirm(
            `You haven't selected any listings to export, would you like to export all of them?`,
            'Export all Listings'
        );
        result.subscribe(() => this.exportListings.emit(this.listings));
    }
}
